import React, { useContext } from 'react';
import { Routes, Route } from 'react-router';
import { SessionContext } from '../../context/AppContainer/AppContainer';
import type { FalconUser } from '../../data/session/interface';
import { hasFalconRole } from '../../helpers/roles';
import {
  TenantDetailsPage,
  DossiersUploadPage,
  TenantsPage,
  TasksPage,
  UsersPage,
  FowlTopologiesPage,
  FulfillmentCapacityPage,
  FeedbackPage,
  FeedbackAdminPage,
  TenantUsersPage,
  TenantUserDetailsPage,
  FileSharePage,
} from '../../pages';
import MainContainer from '../MainContainer/MainContainer';
import { RouteDefs } from './routes';
import DefaultRedirect from './DefaultRedirect';

// We are using react-router v6. See the documentation:
// https://github.com/remix-run/react-router/blob/dev/docs/api-reference.md
// and differences here:
// https://github.com/remix-run/react-router/blob/dev/docs/advanced-guides/migrating-5-to-6.md
// See https://github.com/remix-run/react-router/releases/tag/v6.0.0-beta.4 for some info on routes

/**
 * `current` only lands once the session resolves, so redirecting before then
 * would eject a permitted user who deep-linked to the page.
 */
function fileShareElement(current: FalconUser | undefined): React.ReactElement {
  if (!current) {
    return <React.Fragment />;
  }

  if (hasFalconRole(current, 'file-share')) {
    return <FileSharePage />;
  }

  return <DefaultRedirect path={RouteDefs.Main} />;
}

const Routing: React.FC = () => {
  const { current } = useContext(SessionContext);
  return (
    <Routes>
      <Route path={RouteDefs.Main} element={<MainContainer />}>
        <Route index element={<DefaultRedirect path={'/'} />} />
        <Route path={RouteDefs.Tenants} element={<TenantsPage />} />
        <Route path={RouteDefs.TenantDetails} element={<TenantDetailsPage />} />
        <Route
          path={RouteDefs.TenantUserDetails}
          element={<TenantUserDetailsPage />}
        />
        <Route
          path={RouteDefs.DossiersUpload}
          element={<DossiersUploadPage />}
        />
        <Route path={RouteDefs.Tasks} element={<TasksPage />} />
        <Route path={RouteDefs.Users} element={<UsersPage />} />
        <Route
          path={RouteDefs.FowlTopologies}
          element={<FowlTopologiesPage />}
        />
        <Route
          path={RouteDefs.FulfillmentCapacity}
          element={<FulfillmentCapacityPage />}
        />
        {current?.permission?.['admin'] ? (
          <Route path={RouteDefs.Feedback} element={<FeedbackAdminPage />} />
        ) : (
          <Route path={RouteDefs.Feedback} element={<FeedbackPage />} />
        )}
        <Route path={RouteDefs.TenantUsers} element={<TenantUsersPage />} />
        <Route path={RouteDefs.FileShare} element={fileShareElement(current)} />
      </Route>
    </Routes>
  );
};

export default Routing;
